/**
 * Integrytas Group - Common Security Module (CSM)
 * useOrgLookups - load Company / Division / Business Unit / Office options
 * with localized labels and pick out each table's default row.
 *
 * The hook is shared by:
 *   - UserListPage edit drawer (display + edit existing user's assignments)
 *   - UserProvisionPage submit form (pre-select defaults for a new request)
 *
 * Each list returns active rows only (is_active = 'Y'). The defaults map
 * holds the *_id of the row currently flagged is_default = 'Y' per master,
 * or null when none is set.
 */

import { useEffect, useState } from 'react';
import { apiDb } from './api_client';
import { pickDefaultRow } from './org_default';

export interface OrgOption {
  id: number;
  internal_code: string;
  display_text: string;
  is_default: 'Y' | 'N';
}

export interface OrgLookups {
  companies:      OrgOption[];
  divisions:      OrgOption[];
  business_units: OrgOption[];
  offices:        OrgOption[];
  defaults: {
    company_id:       number | null;
    division_id:      number | null;
    business_unit_id: number | null;
    office_id:        number | null;
  };
  loading: boolean;
  error:   string | null;
}

const TABLES: {
  key:   keyof Omit<OrgLookups, 'defaults' | 'loading' | 'error'>;
  table: string;
  tl:    string;
  pk:    string;
  defaultKey: keyof OrgLookups['defaults'];
}[] = [
  { key: 'companies',      table: 'csm_company',       tl: 'csm_company_tl',       pk: 'company_id',       defaultKey: 'company_id' },
  { key: 'divisions',      table: 'csm_division',      tl: 'csm_division_tl',      pk: 'division_id',      defaultKey: 'division_id' },
  { key: 'business_units', table: 'csm_business_unit', tl: 'csm_business_unit_tl', pk: 'business_unit_id', defaultKey: 'business_unit_id' },
  { key: 'offices',        table: 'csm_office',        tl: 'csm_office_tl',        pk: 'office_id',        defaultKey: 'office_id' },
];

export function useOrgLookups(locale: string): OrgLookups {
  const [state, setState] = useState<OrgLookups>({
    companies: [], divisions: [], business_units: [], offices: [],
    defaults: { company_id: null, division_id: null, business_unit_id: null, office_id: null },
    loading: true, error: null,
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next: OrgLookups = {
          companies: [], divisions: [], business_units: [], offices: [],
          defaults: { company_id: null, division_id: null, business_unit_id: null, office_id: null },
          loading: false, error: null,
        };
        for (const t of TABLES) {
          const { data, error } = await apiDb
            .from(t.table)
            .select(`${t.pk}, internal_code, is_default, is_active, ${t.tl} ( locale_code, display_text )`)
            .eq('is_active', 'Y')
            .order('internal_code');
          if (error) throw error;
          const rows: OrgOption[] = (data ?? []).map((raw: unknown) => {
            const r = raw as Record<string, unknown>;
            const tls = (r[t.tl] ?? []) as TlRow[];
            const exact = tls.find(x => x.locale_code === locale);
            const fb    = tls.find(x => x.locale_code === 'EN_US') ?? tls[0];
            return {
              id: r[t.pk] as OrgOption['id'],
              internal_code: r.internal_code as string,
              display_text: exact?.display_text ?? fb?.display_text ?? (r.internal_code as string),
              is_default: r.is_default as OrgOption['is_default'],
            };
          });
          (next as unknown as Record<string, unknown>)[t.key] = rows;
          const def = pickDefaultRow(rows);
          (next.defaults as unknown as Record<string, unknown>)[t.defaultKey] = def?.id ?? null;
        }
        if (!cancelled) setState(next);
      } catch (e) {
        if (!cancelled) setState(s => ({ ...s, loading: false, error: (e as Error).message }));
      }
    })();
    return () => { cancelled = true; };
  }, [locale]);

  return state;
}
